import React, { useState, useEffect } from 'react';
import { useThemeMode } from '../../ThemeContext';
import UserProfile from './UserProfile';
import CompanyProfile from './CompanyProfile';
import { dataAPI } from '../../helpers/api_service';
import './Profile.css';

const Profile = () => {
  const { mode } = useThemeMode();
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [regions, setRegions] = useState([]);
  const [industryTypes, setIndustryTypes] = useState([]);
  const [individualCompanyData, setIndividualCompanyData] = useState({
    abn_number: '',
    region_id: '',
    industry_type_id: '',
  });

  const handleUserDataUpdate = (updatedUserData) => {
    setUserData(updatedUserData);
  };

  useEffect(() => {
    const getUserData = () => {
      try {
        const userDataFromStorage = localStorage.getItem('userData');
        if (userDataFromStorage) {
          const user = JSON.parse(userDataFromStorage);
          setUserData(user);
        }
      } catch (error) {
        console.error('Error parsing user data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    getUserData();
  }, []);

  useEffect(() => {
    const fetchDropdownData = async () => {
      if (userData?.company_type !== 'individual') return;
      try {
        const data = await dataAPI.getRegionIndustryList();
        if (data?.status === 'success') {
          setRegions(data?.data?.regions || []);
          setIndustryTypes(data?.data?.industry_types || []);
        }
      } catch (error) {
        console.error('Error fetching profile dropdown data:', error);
      }
    };
    fetchDropdownData();
  }, [userData?.company_type]);

  const handleIndividualCompanyFieldChange = (field, value) => {
    setIndividualCompanyData((prev) => ({ ...prev, [field]: value }));
  };

  const handleIndividualCompanySave = async () => {
    try {
      const formData = new FormData();
      formData.append('abn_number', individualCompanyData.abn_number || '');
      formData.append('region_id', individualCompanyData.region_id || '');
      formData.append('industry_type_id', individualCompanyData.industry_type_id || '');

      await dataAPI.editCompany(formData);

      const updatedUserData = {
        ...userData,
        company_detail: {
          ...userData?.company_detail,
          abn_number: individualCompanyData.abn_number || '',
          region_id: individualCompanyData.region_id || '',
          industry_type_id: individualCompanyData.industry_type_id || '',
          region_name:
            regions.find((region) => String(region.id) === String(individualCompanyData.region_id))
              ?.name || userData?.company_detail?.region_name || '',
          industry_type_name:
            industryTypes.find((industry) => String(industry.id) === String(individualCompanyData.industry_type_id))
              ?.name || userData?.company_detail?.industry_type_name || '',
        },
      };

      setUserData(updatedUserData);
      localStorage.setItem('userData', JSON.stringify(updatedUserData));
      window.dispatchEvent(new CustomEvent('userProfileUpdated', { detail: updatedUserData }));
    } catch (error) {
      console.error('Error updating individual company details:', error);
      alert('Failed to update details. Please try again.');
    }
  };

  const handleIndividualCompanyCancel = () => {
    setIndividualCompanyData({
      abn_number: '',
      region_id: '',
      industry_type_id: '',
    });
  };

  if (isLoading) {
    return (
      <div
        className="profile-page"
        style={{
          padding: '24px',
          backgroundColor: mode === 'dark' ? '#0f0f0f' : '#ffffff',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <div style={{
          color: mode === 'dark' ? '#fff' : '#333',
          fontSize: '18px',
          fontFamily: 'Urbanist, Arial, sans-serif'
        }}>
          Loading profile...
        </div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div
        className="profile-page"
        style={{
          padding: '24px',
          backgroundColor: mode === 'dark' ? '#0f0f0f' : '#ffffff',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <div style={{
          color: mode === 'dark' ? '#fff' : '#333',
          fontSize: '18px',
          fontFamily: 'Urbanist, Arial, sans-serif'
        }}>
          No user data found. Please log in again.
        </div>
      </div>
    );
  }

  return (
    <div
      className="profile-page"
      style={{
        padding: '24px',
        backgroundColor: mode === 'dark' ? '#0f0f0f' : '#ffffff',
        minHeight: '100vh',
        fontFamily: 'Urbanist, Arial, sans-serif'
      }}
    >
      {/* Admin Profile Section */}
      <UserProfile
        userData={userData}
        mode={mode}
        onUserDataUpdate={handleUserDataUpdate}
        showCompanyFields={userData.company_type === 'individual'}
        companyData={individualCompanyData}
        regions={regions}
        industryTypes={industryTypes}
        onCompanySave={handleIndividualCompanySave}
        onCompanyCancel={handleIndividualCompanyCancel}
        onCompanyFieldChange={handleIndividualCompanyFieldChange}
      />

      {/* Company Profile Section - Only show if company_type is "company" */}
      {userData.company_type === 'company' && (
        <CompanyProfile userData={userData} mode={mode} onUserDataUpdate={handleUserDataUpdate} />
      )}

    </div>
  );
};

export default Profile;
